import { Card } from "@swot/base-components";
import type { ComplaintDetailDto } from "../../../types/complaint.types.js";
import { CmtsComplaintStatusBadge } from "./CmtsComplaintStatusBadge.js";
import {
  AlertTriangle,
  Clock,
  ShieldAlert,
  User,
  UserCheck,
  Building2,
  Activity,
  Layers,
  FileText,
  Hash,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

export function CmtsComplaintLifecyclePanel({
  record,
}: {
  readonly record?: ComplaintDetailDto | null | undefined;
}) {
  if (!record) {
    return (
      <Card className="p-12 text-center border-none shadow-lg bg-white/50 backdrop-blur-sm animate-pulse">
        <div className="flex flex-col items-center gap-4">
          <Activity className="h-8 w-8 text-indigo-400 animate-spin" />
          <p className="text-slate-500 font-medium">Loading complaint details...</p>
        </div>
      </Card>
    );
  }

  const summaryMetrics = [
    {
      title: "Complaint Code",
      value: record.complaintCode ?? "N/A",
      icon: Hash,
      color: "text-slate-600",
      accent: "bg-slate-500",
      bgColor: "bg-slate-50",
    },
    {
      title: "Priority",
      value: record.priorityName ?? "N/A",
      icon: ShieldAlert,
      color: "text-rose-600",
      accent: "bg-rose-500",
      bgColor: "bg-rose-50/50",
    },
    {
      title: "Severity",
      value: record.severityName ?? "N/A",
      icon: AlertTriangle,
      color: "text-amber-600",
      accent: "bg-amber-500",
      bgColor: "bg-amber-50/50",
    },
    {
      title: "SLA Status",
      value: record.slaStateName ?? "N/A",
      icon: Clock,
      color: "text-indigo-600",
      accent: "bg-indigo-500",
      bgColor: "bg-indigo-50/50",
    },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-700">
      {/* Top Banner Card */}
      <div className="relative overflow-hidden rounded-3xl  p-8 shadow-2xl transition-all duration-500">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
        
        <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-indigo-800 animate-ping" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-900/80">
                System Overview
              </span>
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight text-black sm:text-4xl">
              Lifecycle Tracker
            </h2>
            <p className="text-slate-900 font-medium max-w-md">
              Real-time operational metrics and complaint progression status.
            </p>
          </div>

          <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl backdrop-blur-md border border-white/10">
             <CmtsComplaintStatusBadge
              status={record.statusName}
              stage={record.statusCode}
            />
          </div>
        </div>
      </div>


      {/* Main Content Grid */}
      <div className="grid gap-6">
        {/* Title Section */}
        {record.title && (
          <div className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-slate-100 transition-all duration-300 hover:shadow-md">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-transform group-hover:scale-110">
                <FileText className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Subject Title
                </span>
                <p className="text-lg font-bold text-slate-800 leading-tight">
                  {record.title}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {summaryMetrics.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                style={{ animationDelay: `${idx * 100}ms` }}
                className="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-slate-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl animate-in slide-in-from-bottom-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`rounded-xl ${item.bgColor} p-2.5 transition-colors group-hover:bg-white`}>
                    <Icon className={`h-5 w-5 ${item.color}`} />
                  </div>
                  <div className={`h-1 w-8 rounded-full ${item.accent} opacity-20`} />
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    {item.title}
                  </span>
                  <div className={`text-xl font-black tracking-tight ${item.color} mt-1`}>
                    {item.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail Panels */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Ownership Panel */}
          <div className="rounded-3xl bg-white p-8 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-600">
                  <User className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Ownership</h3>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-300" />
            </div>
            <div className="space-y-4">
              {[
                { label: "Assigned Owner", name: record.ownerName, icon: User },
                { label: "Technical Expert", name: record.expertName, icon: UserCheck },
              ].map((person) => (
                <div key={person.label} className="group flex items-center justify-between rounded-2xl bg-slate-50/50 p-4 transition-all hover:bg-white hover:shadow-md hover:ring-1 hover:ring-slate-100">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-slate-100">
                      <person.icon className="h-5 w-5 text-slate-400" />
                    </div>
                    <span className="text-sm font-bold text-slate-500">{person.label}</span>
                  </div>
                  <span className="text-sm font-extrabold text-slate-800">
                    {person.name ? (
                      <span className="flex items-center gap-2 text-indigo-600">
                        <CheckCircle2 className="h-4 w-4" />
                        {person.name}
                      </span>
                    ) : (
                      <span className="text-slate-300 italic font-medium">Unassigned</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Organizational Tracking */}
          <div className="rounded-3xl bg-white p-8 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-600">
                  <Building2 className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Operational</h3>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-300" />
            </div>

            <div className="space-y-3">
              {[
                { label: "Unit", value: record.organizationUnitName, icon: Building2 },
                { label: "Current Status", value: record.statusName, icon: Activity },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between p-4 rounded-2xl hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <item.icon className="h-5 w-5 text-slate-400" />
                    <span className="text-sm font-bold text-slate-500">{item.label}</span>
                  </div>
                  <span className="text-sm font-extrabold text-slate-800">{item.value ?? "N/A"}</span>
                </div>
              ))}
              
              <div className="mt-4 flex items-center justify-between rounded-2xl bg-indigo-600 p-4 text-white shadow-lg shadow-indigo-200">
                <div className="flex items-center gap-4">
                  <Layers className="h-5 w-5 opacity-80" />
                  <span className="text-sm font-bold opacity-90">Stage Protocol</span>
              
                </div>
                <span className="rounded-lg bg-white/20 px-3 py-1 text-xs font-black tracking-widest">
                  {record.statusCode ?? "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}